import { useTranslation } from "@sagot/i18n";
import { Button, CopyButton, Icon, StatusBadge, type StatusKind } from "@sagot/ui";
import { useState } from "react";
import { useFormatters } from "../shared/format";
import type { RunHook } from "./p4-types";

const H = "engage.workflows.hook";
const SECRET_MASK_LENGTH = 24;
const HOOK_STATUS: Record<string, StatusKind> = { waiting: "warning", received: "success", timed_out: "danger", cancelled: "neutral" };

/**
 * The signed URL of a run parked at "Wait for webhook". The server gives the URL and the secret only to members who can
 * manage workflows, and only while the wait is open; anyone else sees the state alone. The secret stays hidden until asked for.
 */
export function RunHookBox({ hook, canManage }: { hook: RunHook; canManage: boolean }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const [revealed, setRevealed] = useState(false);
  const showCredentials = canManage && hook.url !== null && hook.secret !== null;
  return (
    <section className="space-y-3 rounded-md border border-line bg-surface-2 p-3" aria-label={t(`${H}.runTitle`)} data-testid="run-hook">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-bold">{t(`${H}.runTitle`)}</p>
        <StatusBadge status={HOOK_STATUS[hook.status] ?? "neutral"} label={t(`${H}.statuses.${hook.status}`, { defaultValue: hook.status })} />
      </div>
      <p className="text-xs text-fg-muted">
        {hook.received_at ? t(`${H}.receivedAt`, { when: format.dateTime(hook.received_at) }) : t(`${H}.timeoutAt`, { when: format.dateTime(hook.timeout_at) })}
      </p>
      {showCredentials ? <p className="text-xs text-fg-subtle">{t(`${H}.runSecretHint`)}</p> : null}
      {showCredentials ? (
        <dl className="space-y-3 text-xs">
          <div>
            <dt className="label">{t(`${H}.runUrl`)}</dt>
            <dd className="flex items-center gap-1 rounded-md bg-surface px-3 py-2">
              <code className="min-w-0 flex-1 break-all font-mono" data-testid="run-hook-url">{hook.url}</code>
              <CopyButton value={hook.url ?? ""} label={t(`${H}.copyUrl`)} copiedLabel={t(`${H}.copied`)} />
            </dd>
          </div>
          <div>
            <dt className="label">{t(`${H}.runSecret`)}</dt>
            <dd className="flex items-center gap-1 rounded-md bg-surface px-3 py-2">
              <code className="min-w-0 flex-1 break-all font-mono" data-testid="run-hook-secret">{revealed ? hook.secret : "•".repeat(SECRET_MASK_LENGTH)}</code>
              <Button type="button" variant="ghost" size="sm" iconOnly aria-pressed={revealed} aria-label={t(revealed ? `${H}.hideSecret` : `${H}.showSecret`)} onClick={() => { setRevealed((current) => !current); }}>
                <Icon name={revealed ? "eye-off" : "eye"} />
              </Button>
              <CopyButton value={hook.secret ?? ""} label={t(`${H}.copySecret`)} copiedLabel={t(`${H}.copied`)} />
            </dd>
          </div>
        </dl>
      ) : (
        <p className="text-xs text-fg-subtle">{t(canManage ? `${H}.closed` : `${H}.managersOnly`)}</p>
      )}
    </section>
  );
}
